import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import moment from 'moment';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import * as api from '../../services/api';
import useAccount from '../account/useAccount';
import { API_DATE_FORMAT, QUERY_CACHE_TIME } from '../../constants';
import useToaster from '../useToaster';

/**
 * Hook for managing employee contracts
 * @param {Object} options
 * @param {number} [options.employeeId] - Employee ID (optional for "add employee" flow)
 * @param {boolean} [options.showToasts=true] - Whether to show success/error toasts (disable for custom error handling)
 */
const useEmployeeContracts = ({ employeeId, showToasts = true } = {}) => {
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { addResponseError, addSuccess } = useToaster();

  const storeKey = ['employeeContracts', accountId, employeeId];
  const queryClient = useQueryClient();

  const { isLoading, data } = useQuery(
    storeKey,
    () =>
      api.fetchEmployeeContracts({
        accountId,
        employeeId
      }),
    {
      cacheTime: QUERY_CACHE_TIME,
      enabled: !!accountId && !!employeeId
    }
  );

  const { mutateAsync: assignContract } = useMutation(
    ({ employeeId: empId, contractId, startDate }) => {
      // Use provided employeeId or fall back to hook's employeeId
      const targetEmployeeId = empId || employeeId;
      const targetStartDate = startDate || moment().format(API_DATE_FORMAT);

      return api.assignContractToEmployee({
        accountId,
        employeeId: targetEmployeeId,
        contractId,
        startDate: targetStartDate
      });
    },
    {
      onSuccess: (_, { employeeId: empId }) => {
        const targetEmployeeId = empId || employeeId;
        queryClient.invalidateQueries(['employeeContracts', accountId, targetEmployeeId]);

        if (showToasts) {
          addSuccess(t('contract_assigned_successfully'));
        }
      },
      onError: (e) => {
        if (showToasts) {
          addResponseError(e);
        }
      }
    }
  );

  const { mutateAsync: terminateEmployeeContract } = useMutation(
    ({ employeeContractId }) =>
      api.terminateEmployeeContract({
        accountId,
        employeeId,
        employeeContractId,
        endDate: moment().format(API_DATE_FORMAT)
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(storeKey);
        if (showToasts) {
          addSuccess(t('contract_terminated_successfully'));
        }
      },
      onError: (e) => {
        if (showToasts) {
          addResponseError(e);
        }
      }
    }
  );

  const { mutateAsync: adjustEmployeeContract } = useMutation(
    ({ employeeId: empId, employeeContractId, ...values }) => {
      // Use provided employeeId or fall back to hook's employeeId
      const targetEmployeeId = empId || employeeId;

      return api.adjustEmployeeContract({
        accountId,
        employeeId: targetEmployeeId,
        employeeContractId,
        values
      });
    },
    {
      onSuccess: (_, { employeeId: empId }) => {
        const targetEmployeeId = empId || employeeId;
        queryClient.invalidateQueries(['employeeContracts', accountId, targetEmployeeId]);
        queryClient.invalidateQueries(['employeePto', accountId, targetEmployeeId], { exact: false });

        if (showToasts) {
          addSuccess(t('contract_adjusted_successfully'));
        }
      },
      onError: (e) => {
        if (showToasts) {
          addResponseError(e);
        }
      }
    }
  );

  return {
    isLoading,
    data: data?.data || [],
    assignContract,
    terminateEmployeeContract,
    adjustEmployeeContract
  };
};

export default useEmployeeContracts;
